<template>
	<v-container>
		<v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
			<v-row class="contentRow">
				<!--'Question Number' icon-->
				<v-col cols="12">
					<h1>Complete this {{ name }}</h1>
					<p class="ml-1 pa-3 mt-2" style="border-radius: 5px; background: #00800006;">
						{{ introText }}
					</p>
				</v-col>
				<v-col cols="12" sm="6" md="5" lg="4" v-if="unsaved && !preview">
					<v-alert dense class="mt-2 mb-0" type="warning">
						You have unsaved changes!
					</v-alert>
				</v-col>
			</v-row>
			<div v-for="(question, index) in questions" v-bind:key="index">
				<v-row v-if="shouldDisplay(index)" class="mx-auto">
					<v-row no-gutters class="my-12" v-if="question.type == 'spacer'">
					</v-row>
					<v-row
						no-gutters
						v-else-if="question.type == 'title'"
						class="contentRow primary"
						:style="question.color ? `background-color: ${question.color} !important; border-color: ${question.color} !important` : ''"
					>
						<v-col cols="6" class="mr-auto white--text">
							<h2>{{ question.name }}</h2>
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								dark
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>

						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3 white--text">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right white--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
					<v-row
						no-gutters
						align="center"
						v-else-if="question.type == 'attachment'"
						class="contentRow secondary"
						dark
					>
						<v-col cols="6" class="mr-auto white--text">
							<h2>
								<v-icon dark left>fas fa-file-download</v-icon
								>{{ question.name }}
							</h2>
						</v-col>
						<!--'File uplad' text field-->
						<v-col cols="12" class="white--text">
							<p class="mt-2">
								<i>The CSR Report includes file(s) for you to download:</i>
							</p>
							<file-uploader
								:value="question.fileID"
								:readonly="true"
								:disabled="true"
								:dark="true"
								:name="question.name"
							/>
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								dark
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3 white--text">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right grey--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
					<v-row class="contentRow" align="center" no-gutters v-else>
						<!--'Question Number' icon-->
						<!-- <v-col cols="1" v-if="question.type != 'title'">
						<v-avatar dense tile color="secondary">
							<h3 class="white--text">#{{index}}</h3>
						</v-avatar>
						</v-col>-->
						<!--'Title Name' label-->
						<!--'Question Name' label-->
						<v-col cols="12">
							<h3 class="my-3">
								{{ question.name
								}}<sup v-if="!!question.required"
									><v-icon color="error" x-small right
										>fas fa-asterisk</v-icon
									></sup
								>
							</h3>
						</v-col>
						<!--'Question Name' text field-->
						<v-col cols="12" v-if="question.type == 'singleanswer'">
							<v-text-field
								dense
								align="center"
								label="Answer"
								hide-details="auto"
								placeholder="Enter your answer:"
								:rules="fieldRules(question)"
								outlined
								v-model="answers[index]"
								class="my-3"
							></v-text-field>
						</v-col>
						<!--'Dependency Question' text field-->
						<v-col cols="12" v-if="question.type == 'multiplechoice'">
							<v-select
								dense
								outlined
								:value="answers[index]"
								:items="question.possibilities"
								:menu-props="{ contentClass: 'long-answer-menu', maxHeight: 420, offsetY: true }"
								label="Please Choose"
								hide-details="auto"
								:rules="fieldRules(question)"
								class="my-3 long-answer-select"
								@input="onAnswerChange(index, $event)"
							>
								<template v-slot:selection="{ item }">
									<span class="long-answer-selection">{{ item }}</span>
								</template>
							</v-select>
							<v-alert
								v-if="selectedRecommendation(index)"
								dense
								outlined
								type="info"
								class="mb-3"
							>
								<strong>Recommendation:</strong> {{ selectedRecommendation(index) }}
							</v-alert>
						</v-col>
						<!--'Dependency Question' text field-->
						<v-col cols="12" v-if="question.type == 'multipleanswers'">
							<v-select
								dense
								outlined
								multiple
								v-model="answers[index]"
								:items="question.possibilities"
								menu-props="auto"
								label="Please Choose"
								hide-details="auto"
								single-line
								:rules="fieldRules(question)"
								class="my-3"
							></v-select>
						</v-col>
						<!--'File uplad' text field-->
						<v-col cols="12" v-if="question.type == 'file'">
							<file-uploader v-model="answers[index]" />
						</v-col>
						<!-- Details/hint expanding field -->
						<v-col cols="12">
							<v-btn
								class="my-3"
								text
								v-if="!!question.hint"
								@click.stop="toggleHint(index)"
							>
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>

						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3">
								<v-card-text style="white-space: pre-wrap">{{
									question.hint
								}}</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right grey--text text-caption">
								<i>(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
				</v-row>
			</div>
			<v-row
				v-if="!preview"
				no-gutters
				class="contentRow"
				style="border: 2px solid #4CAF50; /*#3d2645*/"
			>
				<v-col cols="12" class="mr-auto">
					<h2>Ready to submit?</h2>
					<p>
						Note: You will not be able to change your answers at a later time.
					</p>
					<v-btn
						color="success"
						:disabled="preview"
						@click="openDialog"
						type="button"
						align="center"
						class="mt-4"
					>
						Submit
						<v-icon right>fas fa-paper-plane</v-icon>
					</v-btn>
					<v-alert
						v-if="submitError"
						dense
						type="error"
						class="mt-3"
					>
						{{ submitError }}
					</v-alert>
				</v-col>
			</v-row>
			<v-tooltip left v-if="!preview">
				<template v-slot:activator="{ on, attrs }">
					<v-fab-transition>
						<v-btn
							color="success"
							x-large
							rounded
							dark
							bottom
							right
							fixed
							:style="
								unsaved
									? 'border: 2pt outset black; border-color: var(--v-warning-base) !important; '
									: ''
							"
							v-on="on"
							v-bind="attrs"
							@click="saveCsrReport"
						>
							<v-icon left>fas fa-save</v-icon>Save
						</v-btn>
					</v-fab-transition>
				</template>
				<span
					>Click to save{{
						unsaved ? " - You have unsaved changes!" : ""
					}}</span
				>
			</v-tooltip>
		</v-form>
		<v-dialog v-model="dialog" max-width="600px">
				<v-card>
					<v-card-title class="headline">Submit {{ alertLabel }} Report</v-card-title>
					<v-card-text>
						Are you sure you want to submit the survey? Please confirm that your answers are accurate before proceeding. Your responses will impact your score, which cannot be changed for the next 12 months.
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color=primary text @click="dialog = false">Go back to the answers</v-btn>
						<v-btn color=primary text @click="submitForm">Submit the survey</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
	</v-container>
</template>

<script>
//TODO Replace Audit API with CSR API when done
import LoadingState from "../consts/LoadingState"
import FilesAPI from "../api/files/files"
import FileUploaderVue from "../components/FileUploader.vue"
export default {
	name: "FormInput",
	components: {
		"file-uploader": FileUploaderVue
	},
	data() {
		return {
			sheet: false,
			notEmptyRules: [v => !!v || "This field cannot be empty"],
			valid: false,
			answers: [],
			files: [],
			dialog: false,
			answernumbers: [null,null],
			submitError: null,
			attemptedSubmit: false,
		}
	},
	props: {
		preview: Boolean,
		previewID: Number,
		questions: Array,
		savedAnswers: Array,
		fileID: Number,
		id: Number,
		name: String,
		receiveanswernumbers: Array,
		introText: {
			type: String,
			default: "The Orixe Sustainability survey is a comprehensive self-assessment tool that helps organizations measure their maturity in relation to the UN Global Compact's 10 principles. Through a questionnaire-based survey, you can evaluate your sustainability maturity across four key categories: Human Rights, Labour, Environment, and Anti-Corruption. This process provides an overall score for your organization and supply chain while encouraging continuous improvement through honest self-reflection on your status."
		},
		alertLabel: {
			type: String,
			default: "CSR"
		},
	},
	mounted() {
		if (Array.isArray(this.receiveanswernumbers) && this.receiveanswernumbers.length) {
			this.answernumbers = [...this.receiveanswernumbers]
			while (this.answernumbers.length < this.questions.length) {
				this.answernumbers.push(null)
			}
			const filteredAnswers = this.questions.map((question, index) => {
				const value = this.receiveanswernumbers[index]
				if (value === null || value === undefined) {
					return null
				}
				if (question && question.possibilities && question.possibilities[value]) {
					return question.possibilities[value]
				}
				return null
			})
			this.answers = filteredAnswers
		} else {
			this.answernumbers = Array(this.questions.length).fill(null)
			this.answers = Array(this.questions.length).fill(null)
		}
	},
	computed: {
		unsaved() {
			if (this.preview) {
				return false
			}
			let equal =
				this.answers.length == this.savedAnswers.length &&
				this.answers.every((e, i) => {
					return e == this.savedAnswers[i]
				})
			return !equal
		}
	},
	watch: {
		savedAnswers(newVal) {
			this.answers = JSON.parse(JSON.stringify(newVal))
		},
		answers: {
			deep: true,
			handler() {
				this.submitError = null
			}
		}
	},
	methods: {
		fieldRules(question) {
			// Only validate after the user tries to submit — not while filling the form
			if (!this.attemptedSubmit || !question.required) {
				return []
			}
			return this.notEmptyRules
		},
		selectedRecommendation(index) {
			const question = this.questions[index]
			if (!question || !question.recommendations || !Array.isArray(question.recommendations)) {
				return null
			}
			const selectedIndex = this.answernumbers[index]
			if (selectedIndex === null || selectedIndex === undefined) {
				return null
			}
			return question.recommendations[selectedIndex] || null
		},
		fileChanged(index) {
			FilesAPI.createFile(this.files[index].name, this.files[index])
				.then(file => {
					this.$set(this.answers, index, file.id)
				})
				.catch(err => {
					this.files[index] = null
					console.error(err)
				})
		},
		removeFile(index) {
			FilesAPI.deleteFile(this.answers[index])
				.then(() => {
					this.$set(this.answers, index, null)
				})
				.catch(err => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{ root: true }
					)
				})
		},
		downloadFile(fileID, name) {
			FilesAPI.download("" + fileID, name)
		},
		shouldDisplay(index) {
			if (!this.questions[index].dependencyQuestionID) {
				return true
			}
			if (!this.answers[this.questions[index].dependencyQuestionID]) {
				return false
			}
			if (this.questions[index].dependencyQuestionValue.includes(this.answers[this.questions[index].dependencyQuestionID])) {
				return true
			}

			if (typeof this.answers[this.questions[index].dependencyQuestionID] == "object") {
				for (let element of this.answers[this.questions[index].dependencyQuestionID]) {
					if (this.questions[index].dependencyQuestionValue.includes(element)) {
						return true
					}
				}
			}
			return false
		},
		openDialog() {
			this.submitError = null
			this.attemptedSubmit = true
			this.$nextTick(() => {
				const isValid = this.$refs.form && this.$refs.form.validate()
				if (!isValid) {
					this.submitError =
						"Please answer all required questions before submitting. Incomplete fields are highlighted above."
					this.scrollToFirstError()
					return
				}
				this.dialog = true
			})
		},
		submitForm() {
			this.attemptedSubmit = true
			this.$nextTick(() => {
				const isValid = this.$refs.form && this.$refs.form.validate()
				if (!isValid) {
					this.dialog = false
					this.submitError =
						"Please answer all required questions before submitting."
					this.scrollToFirstError()
					return
				}
				this.dialog = false
				this.$emit("submitForm", this.answers)
			})
		},
		scrollToFirstError() {
			const el =
				this.$el &&
				this.$el.querySelector(
					".v-input.error--text, .v-messages.error--text, .error--text"
				)
			if (el && typeof el.scrollIntoView === "function") {
				el.scrollIntoView({ behavior: "smooth", block: "center" })
			}
		},
		saveCsrReport() {
			this.$emit("save", this.answers)
		},
		toggleHint(index) {
			let source = { hintVisible: !this.questions[index].hintVisible }
			let newQuestion = Object.assign({}, this.questions[index], source)
			this.$set(this.questions, index, newQuestion)
		},
		updateAnswerNumber(index, selectedValue) {
			// Get the index of the selected value in the possibilities array
			const question = this.questions[index]
			const selectedIndex = question.possibilities.indexOf(selectedValue)

			// Update answernumbers with the selected index, keeping unselected items as null
			this.$set(this.answernumbers, index, selectedIndex !== -1 ? selectedIndex : null)

			// Ensure the array always has the correct length (e.g., 22)
			while (this.answernumbers.length < this.questions.length) {
				this.answernumbers.push(null)
			}
			this.$emit("updateAnswerNumbers", [...this.answernumbers])
		},
		onAnswerChange(index, selectedValue) {
			this.$set(this.answers, index, selectedValue)
			this.updateAnswerNumber(index, selectedValue)
			// After a failed submit, clear error state for fields that are now answered
			if (this.attemptedSubmit && this.$refs.form) {
				this.$nextTick(() => {
					this.$refs.form.resetValidation()
					this.$refs.form.validate()
				})
			}
		},
	}
}
</script>

<style>
.long-answer-selection {
	white-space: normal;
	word-break: break-word;
	overflow: visible;
	text-overflow: unset;
	line-height: 1.4;
	display: block;
	max-width: 100%;
	padding-right: 8px;
}
.long-answer-select .v-select__selections {
	flex-wrap: wrap;
	max-width: calc(100% - 32px);
	padding-top: 4px;
	padding-bottom: 4px;
}
.long-answer-select.v-text-field--enclosed .v-input__slot {
	min-height: auto !important;
	height: auto !important;
	align-items: flex-start !important;
}
.long-answer-select .v-select__selection--comma {
	white-space: normal !important;
	overflow: visible !important;
	text-overflow: unset !important;
	margin: 0 !important;
}
.long-answer-menu .v-list-item__title {
	white-space: normal !important;
	word-break: break-word;
	overflow: visible !important;
	text-overflow: unset !important;
	height: auto !important;
	line-height: 1.4 !important;
}
.long-answer-menu .v-list-item {
	min-height: auto !important;
	height: auto !important;
	align-items: flex-start !important;
}
</style>
